<template>
  <q-page
    :class="{
      'q-pa-sm': $q.screen.lt.sm,
      'q-pa-md': !$q.screen.lt.sm
    }"
  >
    <folder-breadcrumbs
      :current-path="currentFolderPath"
      show-client
      @go-to-folder="onGoToFolder"
    />

    <div
      class=""
    >
      <div
        class="col-12 q-mb-md row q-col-gutter-md items-center"
      >
        <div
          v-if="currentFolderPath.length > 0"
        >
          <q-btn
            color="primary"
            icon="arrow_back"
            :label="$q.screen.lt.sm ? '' : $t('common.buttonBack')"
            @click="onGoToFolder(currentFolderPath.length - 2)"
          />
        </div>
        <div>
          <add-program-button
            data-e2e="dropdown-add-program"
            :hide-label="$q.screen.lt.sm"
            :program-client-types="availableProgramClientTypes"
            @add-program="onAddProgram"
            @use-program="onUseProgram"
          />
        </div>
        <div>
          <q-btn
            color="primary"
            icon="create_new_folder"
            data-e2e="button-add-folder"
            :label="$q.screen.lt.sm ? '' : $t('library.buttonNewFolder')"
            :disable="offlineMode"
            @click="onNewProgramFolder"
          />
        </div>
        <q-space />
        <div>
          <q-btn
            color="primary"
            :to="{ name: 'Programs - Catalog' }"
          >
            <q-avatar
              size="sm"
              class="q-mr-sm"
            >
              <q-img src="button-assets/logo-128x128.png" />
            </q-avatar>
            <div>{{ $t('store.btnCatalog') }}</div>
          </q-btn>
        </div>
      </div>
    </div>
    <q-table
      ref="filteredPrograms"
      v-model:pagination="pagination"
      v-model:selected="selectedPrograms"
      flat
      bordered
      :rows="foldersAndPrograms"
      :columns="visibleColumns"
      :loading="isLoadingPrograms || isLoadingProgramFolder"
      :no-data-label="$t('programs.tableNoDataLabel')"
      :selection="offlineMode ? 'none' : 'multiple'"
      row-key="id"
      table-header-class="bg-grey-2"
    >
      <template #top>
        <div
          class="row full-width"
        >
          <div
            v-if="!isSearchingProgram"
            class="row items-center q-col-gutter-xs"
          >
            <filter-chips-bar
              :filters="filters"
              :filter-criteria="programFilterCriteria"
              :filter-groups="programColumnGroups"
              @update:filters="onFiltersChanged"
            />
          </div>
          <q-space />
          <search-input
            ref="programSearchInput"
            v-model="programSearch"
            input-class="col"
            @show-input="onShowProgramSearch"
            @blur-input="onHideProgramSearch"
            @hide-input="onHideProgramSearch"
          />
          <div>
            <q-btn
              :disable="offlineMode"
              dense
              round
              flat
              color="dark-variant"
              icon="more_vert"
            >
              <q-menu>
                <q-list style="min-width: 100px">
                  <q-item
                    v-ripple
                    v-close-popup
                    clickable
                    @click="onImportPrograms"
                  >
                    <q-item-section avatar>
                      <q-icon name="fas fa-file-import" />
                    </q-item-section>
                    <q-item-section>
                      {{ $t('programs.itemImportPrograms') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-if="useBetaProgramImport"
                    v-ripple
                    v-close-popup
                    clickable
                    @click="onImportProgramData"
                  >
                    <q-item-section avatar>
                      <q-icon name="upload" />
                    </q-item-section>
                    <q-item-section>
                      {{ $t('programs.itemImportProgramData') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-if="userCanRestorePrograms"
                    v-ripple
                    v-close-popup
                    clickable
                    @click="onRestoreProgram"
                  >
                    <q-item-section avatar>
                      <q-icon name="restore_from_trash" />
                    </q-item-section>
                    <q-item-section>
                      {{ $t('programs.itemRestoreProgram') }}
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
          </div>
        </div>
      </template>
      <template #body-cell-title="props">
        <template
          v-if="props.row.entityType === 'folder'"
        >
          <q-td
            :props="props"
            class="cursor-pointer truncate-label-300"
            :colspan="visibleColumns.length - 1"
            @click="onChangeFolder(props.row.id)"
          >
            {{ props.row.title }}
          </q-td>
        </template>
        <template v-else>
          <q-td
            :props="props"
            class="cursor-pointer custom-text-ellipsis"
            :data-e2e="`row-program-${props.row.title}`"
            @click="onEditProgram(props.row.id)"
          >
            {{ props.row.title }}
            <div
              v-if="shouldDisplayProgramsLocation"
              class="text-caption text-italic"
            >
              {{ props.row.fullFolderPath }}
            </div>
          </q-td>
        </template>
      </template>
      <template #body-cell-tags="props">
        <q-td
          v-if="props.row.entityType === 'program'"
          :props="props"
          class="cursor-pointer"
          @click="onEditProgram(props.row.id)"
        >
          <q-chip
            v-for="tag in props.row.tags"
            :key="tag"
            size="sm"
            :label="tag"
            :style="getTagStyle(tag)"
          />
        </q-td>
      </template>
      <template #header-cell-actions="props">
        <q-th :props="props">
          <q-btn
            flat
            round
            dense
            size="sm"
            color="dark-variant"
            icon="settings"
            @click="onSelectColumns"
          />
        </q-th>
      </template>
      <template #body-cell-status="props">
        <q-td
          v-if="props.row.entityType === 'program'"
          :props="props"
          class="cursor-pointer"
          @click="onEditProgram(props.row.id)"
        >
          <q-icon
            :name="getStatusIcon(props.row.status)"
            class="q-mr-sm"
          />
          {{ $t(`programStatus_${props.row.status}`) }}
        </q-td>
      </template>
      <template #body-cell-actions="props">
        <q-td :props="props">
          <q-btn
            v-if="props.row.entityType === 'program'"
            :disable="offlineMode"
            dense
            round
            flat
            color="primary"
            icon="edit"
            data-e2e="button-edit-program"
            @click="onEditProgram(props.row.id)"
          />
          <q-btn
            dense
            round
            flat
            color="dark-variant"
            icon="more_horiz"
            data-e2e="button-program-actions"
          >
            <q-menu v-if="props.row.entityType === 'folder'">
              <q-list style="min-width: 100px">
                <q-item
                  v-ripple
                  v-close-popup
                  clickable
                  @click="onRenameProgramFolder(props.row.id)"
                >
                  <q-item-section avatar>
                    <q-icon name="create" />
                  </q-item-section>
                  <q-item-section>
                    {{ $t('common.buttonRename') }}
                  </q-item-section>
                </q-item>
                <q-item
                  v-ripple
                  v-close-popup
                  clickable
                  :disable="folders.length === 0"
                  @click="onMoveFolderToFolder(props.row.id, props.row.folderName)"
                >
                  <q-item-section avatar>
                    <q-icon name="folder_open" />
                  </q-item-section>
                  <q-item-section>
                    {{ $t('library.listItemMoveToFolder') }}
                  </q-item-section>
                </q-item>
                <q-item
                  v-ripple
                  v-close-popup
                  clickable
                  @click="onDeleteProgramFolder(props.row.id)"
                >
                  <q-item-section avatar>
                    <q-icon name="delete" />
                  </q-item-section>
                  <q-item-section>
                    {{ $t('library.listItemDeleteProgramFolder') }}
                  </q-item-section>
                </q-item>
                <q-item
                  v-ripple
                  v-close-popup
                  clickable
                  @click="onCopySupportInfo(props.row.id)"
                >
                  <q-item-section avatar>
                    <q-icon name="content_copy" />
                  </q-item-section>
                  <q-item-section>
                    {{ $t('common.copySupportInfo') }}
                  </q-item-section>
                </q-item>
              </q-list>
            </q-menu>
            <q-menu>
              <q-list style="min-width: 100px">
                <q-item
                  v-ripple
                  v-close-popup
                  clickable
                  @click="onPreviewProgram(props.row)"
                >
                  <q-item-section avatar>
                    <q-icon name="info" />
                  </q-item-section>
                  <q-item-section>
                    {{ $t('programs.listItemPreview') }}
                  </q-item-section>
                </q-item>
                <q-item
                  v-ripple
                  v-close-popup
                  :disable="offlineMode"
                  clickable
                  @click="onEditProgram(props.row.id)"
                >
                  <q-item-section avatar>
                    <q-icon name="edit" />
                  </q-item-section>
                  <q-item-section>
                    {{ $t('programs.listItemEdit') }}
                  </q-item-section>
                </q-item>
                <q-item
                  v-ripple
                  v-close-popup
                  :disable="offlineMode"
                  clickable
                  @click="manuallyAddProgramModification(props.row)"
                >
                  <q-item-section avatar>
                    <q-icon name="archive" />
                  </q-item-section>
                  <q-item-section>
                    {{ $t('programs.listItemLogMod') }}
                  </q-item-section>
                </q-item>
                <q-item
                  v-ripple
                  v-close-popup
                  clickable
                  :disable="offlineMode"
                  @click="onDuplicateProgram(props.row)"
                >
                  <q-item-section avatar>
                    <q-icon name="copy_all" />
                  </q-item-section>
                  <q-item-section>
                    {{ $t('programs.listItemDuplicate') }}
                  </q-item-section>
                </q-item>
                <q-item
                  v-ripple
                  v-close-popup
                  clickable
                  :disable="folders.length === 0"
                  @click="openMoveProgramToFolderDialog(props.row.id)"
                >
                  <q-item-section avatar>
                    <q-icon name="folder_open" />
                  </q-item-section>
                  <q-item-section>
                    {{ $t('library.listItemMoveToFolder') }}
                  </q-item-section>
                </q-item>
                <q-item
                  v-ripple
                  v-close-popup
                  clickable
                  :disable="offlineMode || props.row.type === 'behaviour' || !hasScheduledSessions"
                  @click="onAddToSession(props.row)"
                >
                  <q-item-section avatar>
                    <q-icon name="playlist_add" />
                  </q-item-section>
                  <q-item-section>
                    <div class="relative-position">
                      {{ $t('programs.listItemAddToSession') }}
                      <q-tooltip
                        v-if="props.row.type === 'behaviour' || !hasScheduledSessions"
                        anchor="center right"
                        self="center left"
                      >
                        <span v-if="props.row.type === 'behaviour'">
                          {{ $t('programs.tooltipAddToSessionBehaviourDisabled') }}
                        </span>
                        <span v-else-if="!hasScheduledSessions">
                          {{ $t('programs.tooltipAddToSessionNoSessions') }}
                        </span>
                      </q-tooltip>
                    </div>
                  </q-item-section>
                </q-item>
                <q-item
                  v-if="userCanDeleteProgram"
                  v-ripple
                  v-close-popup
                  clickable
                  :disable="offlineMode"
                  data-e2e="dropdown-option-delete-program"
                  @click="onDeleteProgram(props.row.id)"
                >
                  <q-item-section avatar>
                    <q-icon name="delete" />
                  </q-item-section>
                  <q-item-section>
                    {{ $t('programs.listItemDelete') }}
                  </q-item-section>
                </q-item>
                <template
                  v-if="canImportData(props.row)"
                >
                  <q-separator />
                  <q-item
                    v-ripple
                    v-close-popup
                    clickable
                    :disable="offlineMode"
                    @click="onImportData(props.row)"
                  >
                    <q-item-section avatar>
                      <q-icon name="download" />
                    </q-item-section>
                    <q-item-section>
                      {{ $t('programs.listItemImportData') }}
                    </q-item-section>
                  </q-item>
                </template>
                <q-separator />
                <q-item
                  v-ripple
                  v-close-popup
                  clickable
                  :disable="!userCanCopyToLibrary || offlineMode"
                  @click="onCopyToLibrary(props.row.id)"
                >
                  <q-item-section avatar>
                    <q-icon name="local_library" />
                  </q-item-section>
                  <q-item-section>
                    {{ $t('programs.listItemCopy') }}
                  </q-item-section>
                </q-item>
                <q-item
                  v-ripple
                  v-close-popup
                  clickable
                  @click="onCopySupportInfo(props.row.id)"
                >
                  <q-item-section avatar>
                    <q-icon name="content_copy" />
                  </q-item-section>
                  <q-item-section>
                    {{ $t('common.copySupportInfo') }}
                  </q-item-section>
                </q-item>
              </q-list>
            </q-menu>
          </q-btn>
        </q-td>
      </template>
      <template #body-selection="props">
        <q-checkbox
          v-if="props.row.entityType === 'program'"
          v-model="props.selected"
        />
        <div
          v-else
          class="text-center"
        >
          <q-icon
            :name="getFolderIcon(props.row)"
            size="md"
            color="primary-variant"
          />
        </div>
      </template>
      <template #body-cell="props">
        <q-td
          v-if="props.row.entityType === 'program'"
          :props="props"
          class="cursor-pointer"
          @click="offlineMode ? onPreviewProgram(props.row) : onEditProgram(props.row.id)"
        >
          {{ props.value }}
        </q-td>
      </template>
    </q-table>
    <transition
      appear
      enter-active-class="animated zoomIn"
      leave-active-class="animated zoomOut"
    >
      <q-footer
        elevated
        bordered
        class="bg-white text-black"
      >
        <q-banner
          v-if="hasSelectedPrograms"
          class="text-dark bg-white"
        >
          <div class="row full-width items-top">
            <div class="row col items-center">
              <div class="text-caption q-pr-sm q-py-sm">
                {{ $tc('programs.bulkItemsSelected', numSelected, {numSelected}) }}
              </div>
              <div
                class="row items-center"
                :class="{
                  'q-col-gutter-sm': $q.screen.gt.sm,
                  'q-col-gutter-xs': !$q.screen.gt.sm,
                }"
              >
                <BulkActionsDropDownButton
                  :selected-programs="selectedPrograms"
                  @update:programs="updateSelectedPrograms"
                />

                <BulkSetProgramStatusButton
                  class="col-auto"
                  :selected-programs="selectedPrograms"
                  :add-status-activity="addProgramStatusModificationActivity"
                  @updated="selectedPrograms = []"
                />
                <div>
                  <q-btn
                    :label="$t('library.modals.moveProgramToFolderFolder.buttonMoveProgramToFolder')"
                    size="sm"
                    color="secondary"
                    @click="openMoveProgramToFolderDialog(null)"
                  />
                </div>
                <div>
                  <div class="inline-block">
                    <q-btn
                      :label="$t('programs.buttonBulkAddToSession')"
                      size="sm"
                      color="secondary"
                      :disable="isBulkAddToSessionDisabled"
                      @click="onBulkAddToSession"
                    />
                    <q-tooltip v-if="isBulkAddToSessionDisabled && !offlineMode">
                      <span v-if="!hasScheduledSessions">
                        {{ $t('programs.tooltipAddToSessionNoSessions') }}
                      </span>
                      <span v-else>
                        {{ $t('programs.tooltipBulkAddToSessionBehaviourOnly') }}
                      </span>
                    </q-tooltip>
                  </div>
                </div>
                <div
                  v-if="userCanDeleteProgram"
                >
                  <q-btn
                    :label="$q.screen.gt.sm ? $t('programs.buttonDeleteSelectedPrograms') : null"
                    icon="delete"
                    size="sm"
                    color="secondary"
                    @click="onBulkDeletePrograms"
                  />
                </div>
              </div>
            </div>
            <div class="col-auto">
              <q-btn
                icon="close"
                color="dark"
                flat
                round
                dense
                @click="selectedPrograms = []"
              />
            </div>
          </div>
        </q-banner>
      </q-footer>
    </transition>
  </q-page>
</template>

<script>
import FolderBreadcrumbs from 'components/Breadcrumbs/FolderBreadcrumbs';
import CopyProgramHelper from 'components/Library/Modals/CopyProgramHelper';
import FolderDialog from 'components/Modals/FolderDialog';
import ProgramModificationsDialog from 'components/Programs/ProgramModificationsDialog';
import { Timestamp } from 'firebase/firestore';
import { chain, filter, find, get, includes, isArray, isEmpty, map, orderBy, size, some } from 'lodash';
import { mapActions, mapState } from 'pinia';
import AddProgramButton from 'src/components/Programs/AddProgramButton';
import BulkActionsDropDownButton from 'src/components/Programs/BulkActionsDropDownButton.vue';
import BulkSetProgramStatusButton from 'src/components/Programs/BulkSetProgramStatusButton.vue';
import ProgramInfo from 'src/components/Programs/ProgramInfo';
import SearchInput from 'src/components/UI/SearchInput';
import UniversalColumnSelector from 'src/components/UI/UniversalColumnSelector';
import FilterChipsBar from 'components/UI/FilterChipsBar.vue';
import { useProgramFolders } from 'src/composables/programFolders';
import { BEHAVIOR_TRACKING_PROGRAM_SOURCES } from 'src/constants/analytics';
import { KNOWLEDGE_BASE_ARTICLES } from 'src/constants/knowledgeBase';
import { useTime } from 'src/composables/time';
import { ImportDialog, IMPORT_TYPES } from 'src/contexts/onboarding';
import ProgramFoldersDB from 'src/fb/programFolders-db';
import ProgramsDB from 'src/fb/programs-db';
import Auth from 'src/mixins/Auth';
import FiltersMixin from 'src/mixins/FiltersMixin';
import Interactions from 'src/mixins/Interactions';
import ProgramsMixin from 'src/mixins/ProgramsMixin';
import Tags from 'src/mixins/Tags';
import { behaviorTrackingAnalytics } from 'src/services/analytics';
import { auth } from 'src/store/auth';
import { network } from 'src/store/network';
import { programs } from 'src/store/programs';
import { sessions } from 'src/store/sessions';
import { tags } from 'src/store/tags';
import { copySupportInfoToClipboard, sortByArray } from 'src/utils/AppUtils';
import { defineAsyncComponent } from 'vue';

export default {
  name: 'Programs',

  components: {
    AddProgramButton,
    FilterChipsBar,
    FolderBreadcrumbs,
    SearchInput,
    BulkActionsDropDownButton,
    BulkSetProgramStatusButton,
  },

  mixins: [
    Auth,
    CopyProgramHelper,
    ProgramsMixin,
    Tags,
    FiltersMixin,
    Interactions,
  ],

  beforeRouteEnter(to, from, next) {
    next(async (vm) => {
      if (isEmpty(vm.availableTagsFormatted)) {
        vm.reloadTags({ clinicId: get(vm.userDetails, 'clinic', null) });
      }

      vm.getSavedFolderPath();
      vm.loadProgramScopedFolders();
      vm.resetData();
    });
  },

  beforeRouteLeave(to, from, next) {
    const editProgramsRoutes = [
      'Edit Program',
      'Add Program',
      'Add Program from template',
    ];

    if (!includes(editProgramsRoutes, to.name)) {
      this.setCurrentFolderPath([]);
    }
    next();
  },

  setup() {
    const { getLocaleString } = useTime();
    return { ...useProgramFolders(), getLocaleString };
  },

  data() {
    return {
      libraryFolders: [],
      selectedPrograms: [],
      selectedColumns: [],
      filters: {},
      programSearch: '',
      isSearchingProgram: false,

      bulkTags: [],

      pagination: {
        sortBy: 'status',
        descending: false,
        page: 1,
        rowsPerPage: 100,
      },

      defaultColumns: ['title', 'tags', 'status'],
    };
  },

  computed: {
    /*
     * Derived once per column change rather than per render: called from the template it
     * returned a fresh array each time, so the chips bar saw a changed prop on every
     * re-render of the parent.
     */
    programFilterCriteria() {
      return this.getFilterCriteriaFromColumns(this.programColumns);
    }, // programFilterCriteria()

    ...mapState(auth, ['userDetails', 'currentClient', 'connectedClinic', 'useBetaProgramImport',
      'hasSupervisorCredentials', 'isManagerOrAdministrator']),
    ...mapState(programs, ['programs', 'isLoadingPrograms', 'currentFolderPath', 'currentFolder']),
    ...mapState(network, ['offlineMode']),
    ...mapState(sessions, ['scheduledSessions']),

    hasBehaviorProgram() {
      return some(this.programs, ['type', 'behaviour']);
    },

    hasSelectedPrograms() {
      return !isEmpty(this.selectedPrograms);
    }, // hasSelectedPrograms()

    numSelected() {
      // Only count programs, not folders
      return this.selectedPrograms.filter((item) => item.entityType === 'program').length;
    },

    userCanDeleteProgram() {
      return this.hasSupervisorCredentials;
    }, // userCanDeleteProgram()

    userCanCopyToLibrary() {
      return this.userHasPrivilege('library/addPrograms');
    }, // userCanCopyToLibrary()

    userCanRestorePrograms() {
      return this.isManagerOrAdministrator;
    }, // userCanRestorePrograms()

    selectedProgramTags() {
      return chain(this.selectedPrograms)
        .map('tags')
        .flatten()
        .uniq()
        .compact()
        .value();
    },

    availableProgramClientTypes() {
      if (this.currentClient?.type === 'staff') {
        return ['staff'];
      }
      return ['client'];
    },

    programsSearchResult() {
      return this.programSearch
        ? filter(
          this.filteredPrograms,
          ({ programName }) => programName.toLowerCase().includes(this.programSearch.toLowerCase()),
        )
        : this.filteredPrograms;
    }, // programsSearchResult()

    shouldDisplayProgramsLocation() {
      return !!this.programSearch;
    }, // shouldDisplayProgramsLocation()

    foldersAndPrograms() {
      const currentFolderId = this.currentFolder?.id ?? null;

      if (this.shouldDisplayProgramsLocation) {
        const programsWithTableData = this.programsSearchResult.map((program) => {
          this.fullFolderPath = [];
          this.getFolderPath(program.parentFolder);
          return {
            ...program,
            title: program.programName.trim?.() ?? '',
            entityType: 'program',
            fullFolderPath: ['Programs', ...this.fullFolderPath].join('/'),
          };
        });
        const orderedPrograms = orderBy(programsWithTableData, 'parentFolder');

        return orderedPrograms.sort(({ parentFolder }) => {
          return (parentFolder === currentFolderId) ? -1 : 0;
        });
      }

      // Combine folders and programs into table data
      // Filters out items that are not in the current folder
      const foldersWithTableData = this.folders.map(
        (folder) => ({ ...folder, title: ` ${folder.folderName}`, entityType: 'folder' }),
      );
      const programSearchResultWithTableData = this.programsSearchResult.map(
        (program) => ({ ...program, title: program.programName.trim?.() ?? '', entityType: 'program' }),
      );
      const folderAndProgramsTableData = [...foldersWithTableData, ...programSearchResultWithTableData];

      // Filters folders and programs based on their parentFolder:
      // - if inside a folder: show only items whose parentFolder matches current folder
      // - If at root level: show items without a parentFolder or with non-client parentFolder (e.g., from Library)
      const isRootFolder = !this.currentFolder;
      return folderAndProgramsTableData.filter((item) => {
        if (!item.parentFolder) {
          return isRootFolder;
        }

        if (item.parentFolder === currentFolderId) {
          // Item belongs to the currently selected folder — show it
          return true;
        }

        if (isRootFolder) {
          // Show if a parent folder is NOT a client folder (from the Programs page)
          const isClientParentFolder = this.folders.some((folder) => folder.id === item.parentFolder);
          return !isClientParentFolder;
        }

        return false;
      });
    }, // foldersAndPrograms()

    filteredPrograms() {
      return this.getFilteredElements(this.programs, this.filters);
    }, // filteredPrograms()

    visibleColumns() {
      return this.programColumns
        .filter((column) => this.selectedColumns.includes(column.name) || column.persistent)
        .concat(
          {
            name: 'actions',
            label: this.$t('programs.columnsActionsLabel'),
            align: 'right',
            sortable: false,
            persistent: true,
            hideFromSelection: true,
          },
        ).sort(sortByArray({ arrayToSortBy: this.selectedColumns, searchKey: 'name' }));
    },

    hasScheduledSessions() {
      return this.scheduledSessions.some((session) => session.status === 'scheduled' && !session.isDeleted);
    }, // hasScheduledSessions()

    isBulkAddToSessionDisabled() {
      return this.offlineMode
        || !this.hasScheduledSessions
        || !this.selectedPrograms.some((p) => p.type !== 'behaviour');
    }, // isBulkAddToSessionDisabled()
  },

  watch: {
    selectedPrograms(newVal) {
      // filter out folders  when selection changes (e.g., from "select all") since only programs should be selectable
      const programsOnly = newVal.filter((item) => item.entityType === 'program');
      if (programsOnly.length !== newVal.length) {
        this.selectedPrograms = programsOnly;
      }
    },
  },

  methods: {
    ...mapActions(programs, ['deleteProgram', 'syncLibraryPrograms', 'setCurrentFolderPath', 'getSavedFolderPath']),
    ...mapActions(tags, ['saveUnsavedTags']),

    onAddProgram(programType, programClientType) {
      this.$router.push(`/programs/add/${programType}?clientType=${programClientType}`);
    }, // onAddProgram()

    onUseProgram(programId, isTemplate = false) {
      const template = isTemplate ? '?clear' : '';
      this.$router.push(`/programs/add-from-template/${programId}${template}`);
    }, // onUseProgram()

    onShowProgramSearch() {
      this.isSearchingProgram = true;
    }, // onShowProgramSearch()

    onHideProgramSearch() {
      this.isSearchingProgram = false;
    }, // onHideProgramSearch()

    onFiltersChanged(filters) {
      this.filters = filters;
      this.saveFilter();
    }, // onFiltersChanged()

    onSelectColumns() {
      const selectionColumns = chain(this.programColumns)
        .reject({ hideFromSelection: true })
        .map((column) => (
          {
            value: column.name,
            label: column.selectionLabel || column.label || column.exportLabel || '',
            disable: column.mandatory || false,
            group: column.group || null,
          }
        ))
        .value();

      this.openComponentDialog(
        UniversalColumnSelector,
        {
          options: selectionColumns,
          optionGroupLabels: this.programColumnGroups,
          title: this.$t('common.tableEditColumns'),
          selected: this.selectedColumns,
          isAbleToReorder: true,
        },
        (newSelectedColumns) => {
          this.selectedColumns = newSelectedColumns;
          if (isArray(newSelectedColumns)) {
            localStorage.setItem('programColumns', JSON.stringify(newSelectedColumns));
          }
        },
      );
    },

    loadSettings() {
      const selectedColumns = localStorage.getItem('programColumns') || null;
      if (selectedColumns !== null) {
        this.selectedColumns = JSON.parse(selectedColumns);
      }
      if (isEmpty(this.selectedColumns)) {
        this.selectedColumns = this.defaultColumns;
      }
    },

    getProgramTags() {

    }, // getProgramTags()

    onImportPrograms() {
      this.openComponentDialog(
        ImportDialog,
        {
          rootEntityType: IMPORT_TYPES.PROGRAM,
          existingData: this.programs,
          saveContext: {
            destinationClient: this.currentClient.id,
            destinationFolder: null,
          },
          guideUrl: KNOWLEDGE_BASE_ARTICLES.IMPORT_PROGRAMS,
        },
        this.loadProgramScopedFolders,
      );
    }, // onImportPrograms()

    onImportProgramData() {
      this.openComponentDialog(
        ImportDialog,
        {
          rootEntityType: IMPORT_TYPES.SESSION,
          existingData: [],
          guideUrl: KNOWLEDGE_BASE_ARTICLES.IMPORT_PROGRAM_DATA,
        },
        this.loadProgramScopedFolders,
      );
    }, // onImportProgramData()

    async onRestoreProgram() {
      const programsDb = new ProgramsDB();
      let items = [];

      try {
        this.$q.loading.show();
        const deletedPrograms = await programsDb.readAll([
          ['clinic', '==', this.userDetails.clinic],
          ['client', '==', this.currentClient.id],
          ['isDeleted', '==', true],
        ]);
        if (isEmpty(deletedPrograms)) {
          this.notifyNegative(this.$t('programs.notifyNoDeletedPrograms'));
          return;
        }

        items = chain(deletedPrograms)
          .sortBy((program) => (program.deleteTimestamp instanceof Timestamp
            ? -program.deleteTimestamp.toDate().getTime()
            : 0))
          .map((program) => ({
            label: this.$t(
              'programs.textRestoreProgramLabel',
              {
                programName: program.programName,
                deletedTimestamp: program.deleteTimestamp instanceof Timestamp
                  ? this.getLocaleString(program.deleteTimestamp)
                  : '?',
              },
            ),
            value: program.id,
          }))
          .value();
      } finally {
        this.$q.loading.hide();
      }

      this.$q.dialog({
        title: this.$t('programs.dialogRestoreProgramTitle'),
        message: this.$t('programs.dialogRestoreProgramMessage'),
        options: {
          type: 'radio',
          model: null,
          items,
        },
        cancel: true,
        persistent: true,
      }).onOk(async (selectedProgram) => {
        if (!selectedProgram) {
          return;
        }

        try {
          this.$q.loading.show();
          await programsDb.set(
            {
              isDeleted: false,
              deleteTimestamp: null,
            },
            selectedProgram,
          );
          this.notifySuccess(this.$t('programs.notifyProgramRestored'));
        } finally {
          this.$q.loading.hide();
        }
      });
    },

    onImportData(program) {
      const component = program.type === 'behaviour'
        ? defineAsyncComponent(() => import('src/components/Programs/Import/ImportAbcData'))
        : defineAsyncComponent(() => import('src/components/Programs/Import/ImportData'));
      this.openComponentDialog(component, { program });
    }, // onImportData()

    onBulkSetStatus(status) {
      const statusLabel = chain(this.programStatuses).find({ value: status }).get('label').value();
      this.$q
        .dialog({
          title: this.$t('common.dialogTitleConfirm'),
          message: this.$t('programs.dialogBulkSetProgramsStatusMessage', {
            count: size(this.selectedPrograms),
            status: statusLabel,
          }),
          cancel: true,
          persistent: true,
        })
        .onOk(async () => {
          // const program = find(this.programs, { id: programId });
          try {
            const programsDb = new ProgramsDB();
            // One timestamp for the whole bulk operation, so every program gets the same start date.
            const now = Timestamp.now();
            const programStatusActions = map(this.selectedPrograms, async (program) => {
              if (program.dataType === 'incidents') {
                return;
              }
              if (program.status !== status) {
                await programsDb.update({
                  id: program.id,
                  status,
                  [`statusChanges.${status}`]: now,
                });
                await this.addProgramStatusModificationActivity(program, program.status, status);
              }
            });

            await Promise.all(programStatusActions);
            this.selectedPrograms = [];

            this.notifySuccess(this.$t('programs.notifyBulkSetProgramsStatusSuccess'));
          } catch (err) {
            this.notifyNegative(err.message);
          }
        });
    }, // onBulkSetStatus()

    onBulkDeletePrograms() {
      this.$q
        .dialog({
          title: this.$t('common.dialogTitleConfirm'),
          message: this.$t('programs.dialogBulkDeleteProgramsMessage', { count: size(this.selectedPrograms) }),
          cancel: true,
          persistent: true,
        })
        .onOk(async () => {
          try {
            const programDeleteActions = map(this.selectedPrograms, (program) => {
              return this.deleteProgram({ program });
            });
            await Promise.all(programDeleteActions);
            this.selectedPrograms = [];
            this.notifySuccess(this.$t('programs.notifyBulkDeleteProgramsSuccess'));
          } catch (err) {
            this.notifyNegative(err.message);
          }
        });
    }, // onBulkDeletePrograms()

    onEditProgram(programId) {
      this.editProgram(programId);
    },

    onDeleteProgram(programId) {
      this.$q
        .dialog({
          title: this.$t('common.dialogTitleConfirm'),
          message: this.$t('programs.dialogDeleteProgramMessage'),
          cancel: true,
          persistent: true,
        })
        .onOk(async () => {
          const program = find(this.programs, { id: programId });
          try {
            this.deleteProgram({ program });
            this.notifySuccess(this.$t('programs.notifyDeleteProgramSuccess'));
          } catch (err) {
            this.notifyNegative(err.message);
          }
        });
    }, // onDeleteProgram()

    /**
     * Preview a program
     */
    async onPreviewProgram(program) {
      this.openComponentDialog(ProgramInfo, { program });
    }, // onPreviewProgram

    onDuplicateProgram(program) {
      this.$q
        .dialog({
          title: this.$t('common.dialogTitleConfirm'),
          message: this.$t('programs.dialogDuplicateProgramMessage'),
          cancel: true,
          persistent: true,
        })
        .onOk(async () => {
          await this.duplicateProgram(program);
        });
    }, // onDuplicateProgram()

    /**
     * Copy a program to the library
     */
    async onCopyToLibrary(programId) {
      if (isEmpty(this.libraryFolders)) {
        this.$q.loading.show();
        try {
          const programFoldersDb = new ProgramFoldersDB();
          this.libraryFolders = await programFoldersDb
            .readAll([['clinic', '==', this.userDetails.clinic], ['scope', '==', 'Library']]);
        } finally {
          this.$q.loading.hide();
        }
      }
      this.openComponentDialog(
        FolderDialog,
        {
          title: this.$t('common.dialogTitleConfirm'),
          caption: this.$t('programs.dialogCopyToLibraryMessage'),
          submitButtonLabel: this.$t('common.buttonCopy'),
          rootName: this.$t('mainLayout.listItemLibrary'),
          selectedFolder: null,
          folders: this.libraryFolders,
        },
        async ({ parentFolderId }) => {
          await this.copyProgramToLibrary(programId, parentFolderId);
        },
      );
    }, // onCopyToLibrary()

    onCopySupportInfo(programId) {
      copySupportInfoToClipboard({ program: programId });
    }, // onCopySupportInfo()

    resetData() {
      this.libraryFolders = [];
      this.selectedPrograms = [];
      const filterJSON = JSON.parse(localStorage.getItem('programFilter'));

      this.filters = !isEmpty(filterJSON) ? this.setNewFilter(filterJSON) : {};
      this.loadSettings();
    }, // resetData()

    async duplicateProgram(program) {
      // Load program from Firebase
      const programsDb = new ProgramsDB();

      const programProps = {
        programName: `${program.programName} (${this.$t('common.buttonCopy')})`,
        demoVideo: '',
      };
      const targetProps = {
        status: 'waiting',
        introductionDate: null,
        masteryDate: null,
        abortedDate: null,
        holdDate: null,
        progressSettings: null,
      };
      this.$q.loading.show();
      try {
        const programCopy = await programsDb
          .duplicateProgram(program.id, programProps, targetProps);
        behaviorTrackingAnalytics.trackProgramAdded(programCopy, {
          source: BEHAVIOR_TRACKING_PROGRAM_SOURCES.DUPLICATED,
        });

        await this.addProgramCreationActivity(programCopy, null, true);

        this.notifySuccess(this.$t('programs.notifyDuplicateProgramSuccess'));
        // Redirect to Edit program page
        this.$router.push(`/programs/edit/${programCopy.id}`);
      } catch (err) {
        this.notifyNegative(err.message);
      } finally {
        this.$q.loading.hide();
      }
    }, // duplicateProgram()

    saveFilter() {
      const filterJSON = JSON.stringify(this.filters);
      localStorage.setItem('programFilter', filterJSON);
    }, // saveFilter()

    canImportData(program) {
      return ['simple', 'frequency', 'interval', 'duration', 'taskAnalysis', 'behaviour'].includes(program.type);
    }, // canImportData()

    /**
     * Copy a program to the library by copying and adjusting content as neccessary
     */
    async copyProgramToLibrary(programId, folderId) {
      // Load program from Firebase
      const programsDb = new ProgramsDB();

      const programProps = {
        client: null,
        status: 'active',
        clinic: this.userDetails.clinic,
        parentFolder: folderId || null,
        visibility: 'clinic',
        demoVideo: '',
        treatmentArea: null,
        subTreatmentArea: null,
        // @todo Clear baseline or all custom program fields
      };
      const targetProps = {
        status: 'waiting',
        introductionDate: null,
        masteryDate: null,
        abortedDate: null,
        holdDate: null,
        progressSettings: null,
      };
      this.$q.loading.show();
      try {
        const duplicatedProgram = await programsDb
          .duplicateProgram(programId, programProps, targetProps);
        await this.syncLibraryPrograms({ programs: [duplicatedProgram], changeType: 'CREATE' });
        // Show OK message
        this.notifySuccess(this.$t('programs.notifyCopyToLibrarySuccess'));
        // Redirect to Library
        // this.$router.push('/library');
      } catch (err) {
        const errorMessage = `Duplicating program error: ${err}`;
        console.error(errorMessage);
        this.notifyNegative(errorMessage);
      } finally {
        this.$q.loading.hide();
      }
    }, // copyProgramToLibrary()

    editProgram(programId) {
      this.$router.push(`/programs/edit/${programId}`);
    },

    /**
     * Opens program modification dialog window
     *
     * @param {Object} program
     */
    async manuallyAddProgramModification(program) {
      return this.openComponentDialog(
        ProgramModificationsDialog,
        { incomingPrograms: [program], isManuallyOpened: true },
        () => this.notifySuccess(this.$t('programs.notifyAddProgramModSuccess')),
      );
    },

    resetSelection() {
      this.selectedPrograms = [];
    }, // resetSelection()

    onGoToFolder(level) {
      this.goToFolder(level);
      this.resetSelection();
    }, // onGoToFolder()

    onChangeFolder(folderId) {
      this.changeFolder(folderId);
      this.resetSelection();
    }, // onChangeFolder()

    onNewProgramFolder() {
      this.$q.dialog({
        title: this.$t('library.dialogNewProgramFolderTitle'),
        prompt: {
          model: '',
          isValid: (val) => val.length > 0,
          type: 'text',
        },
        cancel: true,
        persistent: false,
      }).onOk((folderName) => {
        const trimmedFolderName = folderName.trim();
        if (trimmedFolderName !== '') {
          this.createProgramFolder(trimmedFolderName);
        }
      });
    }, // onNewProgramFolder()

    onRenameProgramFolder(folderId) {
      const folder = this.findFolder(folderId);

      this.$q.dialog({
        title: this.$t('library.dialogRenameFolderTitle'),
        prompt: {
          model: folder.folderName,
          isValid: (val) => val.trim().length > 0,
          type: 'text',
        },
        cancel: true,
        persistent: false,
      }).onOk((folderName) => {
        const trimmedFolderName = folderName.trim();
        if (trimmedFolderName !== folder.folderName) {
          this.renameProgramFolder(folderId, trimmedFolderName);
        }
      });
    }, // onRenameProgramFolder()

    async onDeleteProgramFolder(folderId) {
      const programFoldersDb = new ProgramFoldersDB();

      if (!await programFoldersDb.isEmptyFolder(this.connectedClinic.id, folderId)) {
        this.notifyNegative(this.$t('library.notifyDeleteProgramFolderCleanFolderError'));
        return;
      }

      this.$q.dialog({
        title: this.$t('common.dialogTitleConfirm'),
        message: this.$t('library.dialogDeleteProgramFolderMessage'),
        cancel: true,
        persistent: true,
      })
        .onOk(() => {
          this.deleteProgramFolder(folderId);
        });
    }, // onDeleteProgramFolder()

    onMoveFolderToFolder(folderId, folderName) {
      this.openComponentDialog(
        FolderDialog,
        {
          title: this.$t('library.modals.moveFolderToFolderFolder.textTitleMoveFolder'),
          caption: this.$t('library.modals.moveFolderToFolderFolder.textCaption'),
          submitButtonLabel: this.$t('common.buttonMove'),
          rootName: this.$t('mainLayout.listItemPrograms'),
          selectedFolder: this.currentFolder?.id ?? null,
          movingFolderName: folderName,
          folders: this.folders.filter((folder) => {
            const { id, parentFolder } = folder;
            const isSameFolder = id === folderId;
            const isChildFolder = parentFolder === folderId;
            const isTopLevelFolder = this.findTopLevelFolderRecursive(folder)?.id === folderId;

            return !isSameFolder && !isChildFolder && !isTopLevelFolder;
          }),
        },
        ({ parentFolderId }) => {
          if (parentFolderId === (this.currentFolder?.id ?? null)) {
            return;
          }
          this.moveFolderToFolder(folderId, parentFolderId);
        },
      );
    }, // onMoveFolderToFolder()

    openMoveProgramToFolderDialog(programId) {
      const selectedProgramsIds = this.selectedPrograms
        .filter((item) => item.entityType === 'program')
        .map(({ id }) => id);

      this.openComponentDialog(
        FolderDialog,
        {
          title: this.$t('library.modals.moveProgramToFolderFolder.textTitleMoveProgram'),
          caption: this.$t('library.modals.moveProgramToFolderFolder.textCaption'),
          submitButtonLabel: this.$t('common.buttonMove'),
          rootName: this.$t('mainLayout.listItemPrograms'),
          selectedFolder: this.currentFolder?.id ?? null,
          folders: this.folders,
        },
        ({ parentFolderId }) => {
          if (parentFolderId === (this.currentFolder?.id ?? null)) {
            return;
          }
          this.moveProgramToFolder(programId, parentFolderId, selectedProgramsIds);
          if (!programId) {
            this.resetSelection();
          }
        },
      );
    }, // openMoveProgramToFolderDialog()

    updateSelectedPrograms() {
      this.resetSelection();
    }, // updateSelectedPrograms()

    async onAddToSession(program) {
      return this.openComponentDialog(
        ProgramModificationsDialog,
        {
          incomingPrograms: [program],
          isSessionAssignmentMode: true,
          isManuallyOpened: true,
        },
        () => this.notifySuccess(this.$t('programs.notifyAddedToSessionSuccess')),
      );
    }, // onAddToSession()

    async onBulkAddToSession() {
      return this.openComponentDialog(
        ProgramModificationsDialog,
        {
          incomingPrograms: this.selectedPrograms,
          isSessionAssignmentMode: true,
          isManuallyOpened: true,
        },
        () => {
          this.notifySuccess(this.$t('programs.notifyAddedToSessionSuccess'));
          this.resetSelection();
        },
      );
    }, // onBulkAddToSession()
  },

};
</script>
<style scoped>
</style>
